import { Box, Tooltip } from '@mui/material'
import { FC } from 'react'
import { useTranslate } from 'react-admin'

import type { AisMark, AisMarks, GanttBar } from '@/data/source'
import { formatDateTime } from '@/domain/dateTime'

import { aisDifferenceColor, useAisDifference } from './useAisDifference'

const DIAMOND = 12
const percent = (fraction: number): string => `${String(fraction * 100)}%`

const useDiamondLabel = () => {
  const translate = useTranslate()
  const { signed, meaning } = useAisDifference()
  return (kind: 'arrival' | 'departure', mark: AisMark, { otherMuelle, arrivalFirstSeen }: AisMarks): string =>
    [
      `${translate(`pages.scales.timeline.ais.${kind}`)}: ${formatDateTime(mark.at)}`,
      ...(kind === 'arrival' && arrivalFirstSeen ? [translate('pages.scales.timeline.ais.first_seen')] : []),
      mark.differenceS === null || mark.level === null
        ? translate('pages.scales.timeline.ais.no_difference')
        : `${translate('pages.scales.timeline.ais.difference', { difference: signed(mark.differenceS) })}, ${meaning(mark.level)}`,
      ...(otherMuelle === null ? [] : [translate('pages.scales.timeline.ais.other_muelle', { muelle: otherMuelle })]),
    ].join(' · ')
}

export const AisDiamonds: FC<{ bar: GanttBar }> = ({ bar }) => {
  const label = useDiamondLabel()
  const { ais, marker } = bar
  if (ais === null) return null
  const diamonds = [
    { kind: 'arrival' as const, mark: ais.arrival },
    ...(ais.departure === null ? [] : [{ kind: 'departure' as const, mark: ais.departure }]),
  ]
  return (
    <>
      {diamonds.map(({ kind, mark }) => {
        const text = label(kind, mark, ais)
        const color = aisDifferenceColor(mark.level)
        return (
          <Tooltip key={kind} title={text}>
            <Box
              role="img"
              aria-label={text}
              data-testid={`gantt-ais-${kind}-${marker.id}`}
              data-color={color}
              position="absolute"
              style={{ left: percent(mark.position) }}
              width={DIAMOND}
              height={DIAMOND}
              zIndex={1}
              sx={{
                transform: 'translateX(-50%) rotate(45deg)',
                bgcolor: color,
                border: 2,
                borderColor: 'background.paper',
                boxSizing: 'content-box',
              }}
            />
          </Tooltip>
        )
      })}
    </>
  )
}
